import React, { useState, useEffect } from "react";
import { customFetch } from '../apiClient';

const UsersTable = () => {
  const hospitalId = sessionStorage.getItem("hospitalId") || "";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUsers = async () => {
    if (!hospitalId) {
      setError("Hospital ID not found.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
    const res = await customFetch(`/hospitals/${hospitalId}/users`);
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [hospitalId]);

  const startEditing = (user) => {
    setEditUserId(user.loginID);
    setEditUserData({
      user_Name: user.user_Name || "",
      password: user.password || "",
      mobileNo: user.mobileNo || "",
    });
    setError(null);
    setMessage(null);
  };

  const cancelEditing = () => {
    setEditUserId(null);
    setEditUserData({});
    setError(null);
  };

  const handleChange = (field, value) => {
    setEditUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveUser = async () => {
    if (!editUserId) return;

    if (!editUserData.user_Name.trim()) {
      setError("User Name cannot be empty.");
      return;
    }
    if (!editUserData.password.trim()) {
      setError("Password cannot be empty.");
      return;
    }
    if (!editUserData.mobileNo.trim()) {
      setError("Mobile Number cannot be empty.");
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    try {
     const res = await customFetch(`/hospitals/${hospitalId}/users/${editUserId}`, {
  method: "PUT",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    user_Name: editUserData.user_Name,
    password: editUserData.password,
    mobileNo: editUserData.mobileNo,
  }),
});
      if (res.status === 403) setError("No permission to edit this user.");
      else if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to update user.");
      } else {
        setMessage("User updated successfully.");
        setEditUserId(null);
        setEditUserData({});
        fetchUsers();
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (loginId) => {
    if (!window.confirm(`Are you sure you want to delete user ${loginId}?`)) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await customFetch(`/hospitals/${hospitalId}/users/${loginId}`, {
  method: "DELETE",
});
      if (res.status === 403) setError("No permission to delete this user.");
      else if (res.status === 404) setError("User not found.");
      else if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to delete user.");
      } else {
        setMessage("User deleted successfully.");
        if (editUserId === loginId) {
          setEditUserId(null);
          setEditUserData({});
        }
        fetchUsers();
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.loginID?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.user_Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.mobileNo?.includes(searchQuery)
  );

  return (
    <>
    <div className="users-management-container">
      <div className="users-card">
        {/* Header */}
        <div className="users-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="header-text">
              <h5>User Management</h5>
            </div>
          </div>
          <div className="header-right">
            <div className="user-count">
              <i className="bi bi-person-check-fill"></i>
              <span>{users.length} Users</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError(null)}><i className="bi bi-x"></i></button>
          </div>
        )}
        {message && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{message}</span>
            <button className="alert-close" onClick={() => setMessage(null)}><i className="bi bi-x"></i></button>
          </div>
        )}

        {/* Search & Actions */}
        <div className="table-toolbar">
          <div className="search-box">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search by ID, Name, Mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="btn-refresh" onClick={fetchUsers} disabled={loading}>
            <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
            Refresh
          </button>
        </div>

        {/* Table Container */}
        <div className="table-container">
          {loading && users.length === 0 ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              <p>No users found</p>
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Login ID</th>
                  <th>User Name</th>
                  <th>Password</th>
                  <th>Mobile</th>
                  <th className="action-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.loginID} className={editUserId === user.loginID ? 'editing' : ''}>
                    <td className="nowrap">
                      <span className="id-badge">{user.loginID}</span>
                    </td>
                    <td>
                      {editUserId === user.loginID ? (
                        <input
                          type="text" className="form-input"
                          value={editUserData.user_Name ?? ""}
                          onChange={(e) => handleChange("user_Name", e.target.value)}
                          placeholder="Name"
                        />
                      ) : (
                        <div className="table-text">{user.user_Name}</div>
                      )}
                    </td>
                    <td>
                      {editUserId === user.loginID ? (
                        <input
                          type="password" className="form-input"
                          placeholder="Password"
                          value={editUserData.password ?? ""}
                          onChange={(e) => handleChange("password", e.target.value)}
                        />
                      ) : (
                        <div className="password-display">••••••••</div>
                      )}
                    </td>
                    <td className="nowrap">
                      {editUserId === user.loginID ? (
                        <input
                          type="text" className="form-input"
                          value={editUserData.mobileNo ?? ""}
                          onChange={(e) => handleChange("mobileNo", e.target.value)}
                          placeholder="Mobile" maxLength="10"
                        />
                      ) : (
                        <div className="table-text">{user.mobileNo}</div>
                      )}
                    </td>
                    <td className="action-col">
                      <div className="action-buttons">
                        {editUserId === user.loginID ? (
                          <>
                            <button className="btn-action btn-save" onClick={saveUser} disabled={loading} title="Save">
                              <i className="bi bi-check-lg"></i>
                            </button>
                            <button className="btn-action btn-cancel" onClick={cancelEditing} disabled={loading} title="Cancel">
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn-icon btn-edit" onClick={() => startEditing(user)} title="Edit">
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button className="btn-icon btn-delete" onClick={() => deleteUser(user.loginID)} title="Delete">
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style jsx>{`
        .users-management-container {
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .users-card {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        /* Header */
        .users-header {
          background: var(--theme-color, #0088cc);
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          border-bottom: 2px solid #00d4aa;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .header-icon {
          width: 24px; height: 24px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: white;
        }

        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }

        .user-count {
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 10px;
          border-radius: 4px;
          display: flex; align-items: center; gap: 6px;
          color: white; font-size: 11px; font-weight: 600;
        }
        .user-count i { font-size: 13px; }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 12px; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        /* Toolbar */
        .table-toolbar {
          padding: 8px 12px;
          display: flex; justify-content: space-between; align-items: center;
          gap: 10px; border-bottom: 1px solid #e2e8f0;
          background: #f7fafc;
        }

        .search-box {
          flex: 1; max-width: 300px; position: relative;
        }

        .search-box i {
          position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
          color: #a0aec0; font-size: 12px;
        }

        .search-box input {
          width: 100%; padding: 6px 10px 6px 28px;
          border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 12px; outline: none; transition: 0.2s;
        }
        .search-box input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        .btn-refresh {
          padding: 6px 12px; border-radius: 4px;
          border: 1px solid #cbd5e0; background: white; color: #4a5568;
          font-size: 12px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; gap: 6px; transition: 0.2s;
        }
        .btn-refresh:hover:not(:disabled) { background: #edf2f7; }
        .spinning { animation: spin 1s linear infinite; }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* Table */
        .table-container {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          min-height: 200px;
        }

        .loading-state, .empty-state { text-align: center; padding: 40px 20px; font-size: 12px; color: #718096; }
        .spinner {
          width: 24px; height: 24px; border: 3px solid #edf2f7; border-top-color: var(--theme-color, #3182ce);
          border-radius: 50%; margin: 0 auto 10px; animation: spin 1s linear infinite;
        }

        .users-table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: left; }
        .users-table thead { background: #edf2f7; border-bottom: 1px solid #cbd5e0; }
        .users-table th { padding: 8px 12px; font-weight: 600; color: #4a5568; white-space: nowrap; }
        .users-table td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #2d3748; vertical-align: middle; }
        .users-table tbody tr:hover { background: #f7fafc; }
        .users-table tbody tr.editing { background: #ebf8ff; }

        .nowrap { white-space: nowrap; }
        .action-col { width: 80px; text-align: center; }

        .id-badge {
          background: var(--theme-color, #3182ce); color: white;
          padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 11px;
        }
        .table-text { font-weight: 500; }
        .password-display { color: #a0aec0; letter-spacing: 2px; }

        .form-input {
          width: 100%; padding: 4px 8px; border: 1px solid #cbd5e0;
          border-radius: 4px; font-size: 12px; outline: none; background: white;
        }
        .form-input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        /* Buttons */
        .action-buttons { display: flex; gap: 4px; justify-content: flex-start; }
        .btn-icon, .btn-action {
          border: none; border-radius: 4px; cursor: pointer;
          display: flex; align-items: center; justify-content: center; transition: 0.2s;
        }
        
        .btn-icon { width: 26px; height: 26px; font-size: 13px; }
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }

        .btn-action { padding: 4px 8px; font-size: 13px; }
        .btn-save { background: #38a169; color: white; }
        .btn-save:hover { background: #2f855a; }
        .btn-cancel { background: #a0aec0; color: white; }
        .btn-cancel:hover { background: #718096; }

        /* Responsive Breakpoints */
        @media (max-width: 600px) {
          .table-toolbar { flex-direction: column; align-items: stretch; }
          .search-box { max-width: 100%; }
          .btn-refresh { justify-content: center; }
        }
      `}</style>
    </div>
    </>
  );
  
};


export default UsersTable;